import { Fragment, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import useInfiniteScroll from "../../../hooks/useInfiniteScroll";
import { dayKey, formatDayDivider } from "../../../utils/chatTime";
import AlertCircleIcon from "../../icons/AlertCircleIcon";
import CustomerSupportSidebarIcon from "../../icons/CustomerSupportSidebarIcon";
import ScrollToBottomIcon from "../../icons/ScrollToBottomIcon";
import Loader from "../../loader/Loader";
import Skeleton from "../../skeleton/Skeleton";
import ChatState from "../chatState/ChatState";
import styles from "./messageThread.module.css";

// Messages from one side less than this apart share a group: one tail,
// tight spacing.
const GROUP_GAP_MS = 5 * 60 * 1000;
// Within this of the bottom counts as reading the newest messages.
const NEAR_BOTTOM_PX = 120;

const SKELETON_BUBBLES = [
  { side: "player", width: "46%" },
  { side: "player", width: "28%" },
  { side: "support", width: "52%" },
  { side: "player", width: "36%" },
  { side: "support", width: "30%" },
  { side: "support", width: "44%" },
];

const ThreadSkeleton = () => (
  <div className={styles.skeleton} aria-hidden="true">
    {SKELETON_BUBBLES.map((bubble, index) => (
      <div
        key={index}
        className={bubble.side === "support" ? styles.skeletonSupport : styles.skeletonPlayer}
      >
        <Skeleton width={bubble.width} height={42} radius={16} />
      </div>
    ))}
  </div>
);

// A message sent from here carries a delivery status ("sending" | "failed")
// until the server's copy replaces it; that copy keeps the local id as
// localKey, so it stays the same row on screen.
const messageKey = (message) => message.localKey || message._id;

// The thread by day, each message knowing where its run starts and ends.
const buildDays = (items, timeZone, sideOf) => {
  const days = [];
  let previous = null;
  for (const message of items) {
    const at = new Date(message.createdAt);
    const key = dayKey(at, timeZone);
    let day = days[days.length - 1];
    if (!day || day.key !== key) {
      day = { key, at, rows: [] };
      days.push(day);
      previous = null;
    }
    const side = sideOf(message);
    const joins = previous && previous.side === side && at - previous.at < GROUP_GAP_MS;
    if (joins) previous.row.groupEnd = false;
    const row = { message, side, groupStart: !joins, groupEnd: true };
    day.rows.push(row);
    previous = { side, at, row };
  }
  return days;
};

// One conversation, oldest first (its parent keys it per conversation, so
// each one starts fresh): day dividers, runs grouped by side, a skeleton
// while the newest page loads and a retry if it fails, older pages on the way
// up without moving the view, new messages followed while at the bottom, and
// a jump button with the unread count otherwise.
//   status       the newest page: "idle" | "loading" | "succeeded" | "failed"
//   olderStatus  scrolling back: "idle" | "loading" | "failed"
//   sideOf(message)  "player" | "support"
//   renderMessage({ message, side, groupStart, groupEnd })  the bubble
//   onLoadNewest()      load (or retry) the newest page
//   onLoadOlder(oldest) load the page before that message
//   onSeen()            unread messages are on screen: mark them read
const MessageThread = ({
  items,
  status,
  olderStatus,
  hasMore,
  unread,
  timeZone,
  sideOf,
  renderMessage,
  onLoadNewest,
  onLoadOlder,
  onSeen,
  emptyTitle = "لا توجد رسائل بعد",
  emptyText = "",
}) => {
  const scrollRef = useRef(null);
  const atBottom = useRef(true);
  const olderAnchor = useRef(null);
  const newestKey = useRef(null);
  const [showJump, setShowJump] = useState(false);

  const days = useMemo(() => buildDays(items, timeZone, sideOf), [items, timeZone, sideOf]);

  const scrollToBottom = useCallback((smooth) => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    scroller.scrollTo({ top: scroller.scrollHeight, behavior: smooth ? "smooth" : "auto" });
  }, []);

  const loadOlder = useCallback(() => {
    const scroller = scrollRef.current;
    const oldest = items[0];
    if (!scroller || !oldest || oldest.status) return;
    olderAnchor.current = { height: scroller.scrollHeight, top: scroller.scrollTop };
    onLoadOlder(oldest);
  }, [items, onLoadOlder]);

  useInfiniteScroll({
    ref: scrollRef,
    // A failed page waits for its retry instead of re-firing on every scroll.
    hasMore: hasMore && status === "succeeded" && olderStatus === "idle",
    isLoading: olderStatus === "loading",
    onLoadMore: loadOlder,
    direction: "up",
    threshold: 120,
  });

  const markSeen = useCallback(() => {
    if (unread > 0 && atBottom.current && document.visibilityState === "visible") onSeen();
  }, [unread, onSeen]);

  const handleScroll = () => {
    const scroller = scrollRef.current;
    if (!scroller) return;
    const distance = scroller.scrollHeight - scroller.scrollTop - scroller.clientHeight;
    atBottom.current = distance < NEAR_BOTTOM_PX;
    setShowJump(!atBottom.current);
    markSeen();
  };

  // New unread messages while reading at the bottom, or on coming back to
  // the browser tab.
  useEffect(() => {
    if (status === "succeeded") markSeen();
  }, [status, markSeen]);

  useEffect(() => {
    document.addEventListener("visibilitychange", markSeen);
    return () => document.removeEventListener("visibilitychange", markSeen);
  }, [markSeen]);

  // Before paint, so nothing jumps: an older page keeps the same messages in
  // view; the first page opens at the bottom; a new message is followed if
  // it's ours or the bottom was in view; and a thread read at the bottom
  // stays there when a message grows (a failed send's retry row).
  useLayoutEffect(() => {
    const scroller = scrollRef.current;
    if (!scroller) return;

    if (olderAnchor.current && olderStatus !== "loading") {
      const { height, top } = olderAnchor.current;
      olderAnchor.current = null;
      scroller.scrollTop = scroller.scrollHeight - height + top;
      return;
    }

    const newest = items[items.length - 1];
    const key = newest ? messageKey(newest) : null;
    const first = newestKey.current === null;
    const arrived = key !== newestKey.current;
    newestKey.current = key;
    if (first || (arrived && newest?.status === "sending") || atBottom.current) {
      atBottom.current = true;
      scrollToBottom(arrived && !first);
    }
  }, [items, olderStatus, scrollToBottom]);

  const showSkeleton = (status === "idle" || status === "loading") && items.length === 0;
  const failedEmpty = status === "failed" && items.length === 0;
  const empty = status === "succeeded" && items.length === 0;

  return (
    <div className={styles.container}>
      <div
        className={styles.scrollBody}
        ref={scrollRef}
        onScroll={handleScroll}
        role="log"
        aria-busy={status === "loading" || olderStatus === "loading"}
      >
        {showSkeleton && <ThreadSkeleton />}

        {failedEmpty && (
          <ChatState
            tone="error"
            icon={<AlertCircleIcon />}
            title="تعذّر تحميل الرسائل"
            text="تحقق من الاتصال ثم حاول مرة أخرى."
            actionLabel="إعادة المحاولة"
            onAction={onLoadNewest}
          />
        )}

        {empty && (
          <ChatState icon={<CustomerSupportSidebarIcon />} title={emptyTitle} text={emptyText} />
        )}

        {items.length > 0 && !hasMore && status === "succeeded" && (
          <div className={styles.startMarker}>بداية المحادثة</div>
        )}

        {days.map((day) => (
          <section key={day.key} className={styles.day}>
            <div className={styles.dayDivider}>
              <span>{formatDayDivider(day.at, timeZone)}</span>
            </div>
            {day.rows.map((row) => (
              <Fragment key={messageKey(row.message)}>{renderMessage(row)}</Fragment>
            ))}
          </section>
        ))}
      </div>

      {olderStatus === "loading" && (
        <div className={styles.topPill} role="status">
          <Loader size={14} />
          <span>جارٍ تحميل الرسائل الأقدم…</span>
        </div>
      )}

      {olderStatus === "failed" && (
        <div className={`${styles.topPill} ${styles.topPillError}`} role="alert">
          <span>تعذّر تحميل الرسائل الأقدم</span>
          <button type="button" className={styles.pillButton} onClick={loadOlder}>
            إعادة المحاولة
          </button>
        </div>
      )}

      {status === "failed" && items.length > 0 && (
        <div className={`${styles.topPill} ${styles.topPillError}`} role="alert">
          <span>تعذّر تحديث المحادثة</span>
          <button type="button" className={styles.pillButton} onClick={onLoadNewest}>
            إعادة المحاولة
          </button>
        </div>
      )}

      {showJump && (
        <button
          type="button"
          className={styles.jump}
          onClick={() => scrollToBottom(true)}
          aria-label="الانتقال إلى آخر رسالة"
        >
          <ScrollToBottomIcon className={styles.jumpIcon} />
          {unread > 0 && <span className={styles.jumpCount}>{unread > 99 ? "99+" : unread}</span>}
        </button>
      )}
    </div>
  );
};

export default MessageThread;
